import { useState } from 'react';
import type { SuppressorRow } from '../lib/api.js';

interface Props {
  initial: Partial<SuppressorRow> & { id?: number };
  onCancel: () => void;
  onSave: (changes: Partial<SuppressorRow>) => void | Promise<void>;
}

const DIMENSION_COLS = [
  'suppressor_diameter',
  'suppressor_length',
  'cover_diameter',
] as const;

const COMBO_LENGTH_COLS = [
  'len_dt_covered',
  'len_dt_exposed',
  'len_re_exposed',
  'len_qda_covered',
  'len_qdb_covered',
  'len_qda_exposed',
  'len_qdb_exposed',
] as const;

const NUMERIC_COLS = [
  ...DIMENSION_COLS,
  ...COMBO_LENGTH_COLS,
] as const;

const STRING_COLS = [
  'brand',
  'model',
  'suppressor_link',
  'base_sku',
  'sku_dt_covered',
  'sku_dt_exposed',
  'sku_re_exposed',
  'sku_qda_covered',
  'sku_qdb_covered',
  'sku_qda_exposed',
  'sku_qdb_exposed',
  'source_notes',
] as const;

const DIMENSION_LABELS = {
  suppressor_diameter: 'Suppressor diameter',
  suppressor_length: 'Suppressor length',
  cover_diameter: 'Cover diameter',
} as const satisfies Partial<Record<(typeof NUMERIC_COLS)[number], string>>;

const COMBO_LABELS = {
  len_dt_covered: 'Direct Thread / Standard + Cover the Front',
  len_dt_exposed: 'Direct Thread / Standard + Leave Front Exposed',
  len_re_exposed: 'Leave Rear Exposed + Leave Front Exposed',
  len_qda_covered: 'QD Mount Small (Type A) + Cover the Front',
  len_qdb_covered: 'QD Mount Large (Type B) + Cover the Front',
  len_qda_exposed: 'QD Mount Small (Type A) + Leave Front Exposed',
  len_qdb_exposed: 'QD Mount Large (Type B) + Leave Front Exposed',
} as const satisfies Record<(typeof COMBO_LENGTH_COLS)[number], string>;

export function SuppressorEditor({ initial, onCancel, onSave }: Props): JSX.Element {
  const [draft, setDraft] = useState<Partial<SuppressorRow>>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof SuppressorRow>(key: K, value: SuppressorRow[K] | null): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      await onSave(draft);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <h2>{initial.id ? `Edit suppressor #${initial.id}` : 'New suppressor'}</h2>
        {error && <div className="notice error">{error}</div>}

        <h3 style={{ marginTop: 16 }}>Identity</h3>
        <div className="fields">
          <label>Brand
            <input value={draft.brand ?? ''} onChange={(e) => update('brand', e.target.value)} />
          </label>
          <label>Model
            <input value={draft.model ?? ''} onChange={(e) => update('model', e.target.value)} />
          </label>
          <label className="full">Suppressor link
            <input value={draft.suppressor_link ?? ''} onChange={(e) => update('suppressor_link', e.target.value || null)} />
          </label>
          <label>Cover type
            <select value={draft.cover_type ?? 'MEDIUM'} onChange={(e) => update('cover_type', e.target.value as SuppressorRow['cover_type'])}>
              <option value="MEDIUM">MEDIUM</option>
              <option value="HEAVY">HEAVY</option>
              <option value="CUSTOM MEDIUM">CUSTOM MEDIUM</option>
              <option value="CUSTOM HEAVY">CUSTOM HEAVY</option>
            </select>
          </label>
          <label>Base SKU
            <input className="sku" value={draft.base_sku ?? ''} onChange={(e) => update('base_sku', e.target.value)} />
          </label>
        </div>

        <h3 style={{ marginTop: 16 }}>Dimensions</h3>
        <div className="fields">
          {DIMENSION_COLS.map((c) => (
            <label key={c}>{DIMENSION_LABELS[c]}
              <input
                type="number"
                step="0.01"
                value={(draft[c] as number | null | undefined) ?? ''}
                onChange={(e) => update(c, e.target.value === '' ? null : Number(e.target.value))}
              />
            </label>
          ))}
        </div>

        <h3 style={{ marginTop: 16 }}>Combo lengths & SKUs</h3>
        <p className="form-help">
          Leave a combo length blank to disable that option in the customer configurator.
        </p>
        <div className="combo-fields">
          {COMBO_LENGTH_COLS.map((c) => {
            const skuCol = c.replace(/^len_/, 'sku_') as (typeof STRING_COLS)[number];
            const hasLength = draft[c] != null;
            return (
              <div key={c} className={`combo-row${hasLength ? '' : ' is-disabled'}`}>
                <div className="combo-label">
                  <span>{COMBO_LABELS[c]}</span>
                  <span className={`combo-status ${hasLength ? 'enabled' : 'disabled'}`}>
                    {hasLength ? 'Enabled' : 'Unavailable'}
                  </span>
                </div>
                <div className="combo-inputs">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Length (blank disables)"
                    value={(draft[c] as number | null | undefined) ?? ''}
                    onChange={(e) => update(c, e.target.value === '' ? null : Number(e.target.value))}
                  />
                  <input
                    className="sku"
                    placeholder="SKU (defaults to base)"
                    value={(draft[skuCol] as string | null | undefined) ?? ''}
                    onChange={(e) => update(skuCol, e.target.value || null)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <h3 style={{ marginTop: 16 }}>Status</h3>
        <div className="fields">
          <label>
            <span>Flow-through</span>
            <select
              value={draft.flow_through ? '1' : '0'}
              onChange={(e) => update('flow_through', e.target.value === '1' ? 1 : 0)}
            >
              <option value="0">No</option>
              <option value="1">Yes — front must remain exposed</option>
            </select>
          </label>
          <label>
            <span>Needs review</span>
            <select
              value={draft.needs_review ? '1' : '0'}
              onChange={(e) => update('needs_review', e.target.value === '1' ? 1 : 0)}
            >
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </label>
          <label>
            <span>Active</span>
            <select
              value={draft.is_active === 0 ? '0' : '1'}
              onChange={(e) => update('is_active', e.target.value === '1' ? 1 : 0)}
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </label>
          <label className="full">Source notes
            <input value={draft.source_notes ?? ''} onChange={(e) => update('source_notes', e.target.value || null)} />
          </label>
        </div>

        <div className="modal-actions">
          <button onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
